import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, Key, Plus, Trash2, X } from "lucide-react";
import { useState } from "react";
import { api } from "../lib/api";
import type { ApiKeyCreated, ApiKeyScope } from "../lib/types";

interface Props {
  open: boolean;
  onClose: () => void;
}

const ALL_SCOPES: ApiKeyScope[] = [
  "notes:read",
  "notes:write",
  "search:read",
  "clip:write",
  "tasks:read",
  "tasks:write",
];

export function ApiKeysDialog({ open, onClose }: Props) {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [scopes, setScopes] = useState<ApiKeyScope[]>(["notes:read", "search:read"]);
  const [created, setCreated] = useState<ApiKeyCreated | null>(null);

  const keysQ = useQuery({
    queryKey: ["apiKeys"],
    queryFn: () => api.listApiKeys(),
    enabled: open,
  });

  const createMut = useMutation({
    mutationFn: () => api.createApiKey({ name, scopes }),
    onSuccess: (k) => {
      setCreated(k);
      setName("");
      qc.invalidateQueries({ queryKey: ["apiKeys"] });
    },
  });

  const revokeMut = useMutation({
    mutationFn: (id: string) => api.revokeApiKey(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["apiKeys"] }),
  });

  if (!open) return null;

  function toggleScope(s: ApiKeyScope) {
    setScopes((cur) =>
      cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s],
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 pt-16"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl rounded-xl border border-zinc-800 bg-zinc-950 p-4 shadow-2xl"
      >
        <header className="mb-3 flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-sm font-medium">
            <Key className="h-4 w-4 text-emerald-400" />
            API-Keys für Sibling-Apps
          </h2>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-200">
            <X className="h-4 w-4" />
          </button>
        </header>

        {created && (
          <div className="mb-3 rounded border border-emerald-700 bg-emerald-950/30 p-3 text-xs">
            <div className="mb-1 font-medium text-emerald-300">Neuer Key — JETZT kopieren:</div>
            <div className="flex items-center gap-2">
              <code className="flex-1 break-all rounded bg-zinc-900 px-2 py-1 font-mono text-[11px] text-emerald-200">
                {created.key}
              </code>
              <button
                onClick={() => {
                  void navigator.clipboard.writeText(created.key);
                }}
                className="rounded border border-zinc-700 p-1.5 hover:border-emerald-500"
                title="Kopieren"
              >
                <Copy className="h-3 w-3" />
              </button>
            </div>
            <p className="mt-2 text-[10px] text-zinc-400">
              Der Klartext-Key wird nirgendwo gespeichert. Nach Schließen weg.
            </p>
            <button
              onClick={() => setCreated(null)}
              className="mt-2 text-[10px] text-zinc-400 hover:text-zinc-200"
            >
              OK, ich habs kopiert
            </button>
          </div>
        )}

        {/* Create form */}
        <div className="mb-4 space-y-2 rounded border border-zinc-800 bg-zinc-900/40 p-3">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">Neuer Key</div>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Name (z.B. DocVault-Integration)"
            className="w-full rounded border border-zinc-700 bg-zinc-900 px-2 py-1 text-sm"
          />
          <div className="flex flex-wrap gap-2">
            {ALL_SCOPES.map((s) => (
              <label
                key={s}
                className="flex cursor-pointer items-center gap-1 rounded border border-zinc-700 px-2 py-1 text-[11px]"
              >
                <input
                  type="checkbox"
                  checked={scopes.includes(s)}
                  onChange={() => toggleScope(s)}
                  className="h-3 w-3"
                />
                <code>{s}</code>
              </label>
            ))}
          </div>
          <button
            onClick={() => createMut.mutate()}
            disabled={!name.trim() || scopes.length === 0 || createMut.isPending}
            className="rounded bg-emerald-600 px-3 py-1.5 text-xs hover:bg-emerald-500 disabled:opacity-50"
          >
            <Plus className="mr-1 inline-block h-3 w-3" />
            Key erstellen
          </button>
        </div>

        {/* List */}
        <ul className="max-h-96 space-y-1 overflow-y-auto">
          {keysQ.data?.length === 0 && (
            <li className="text-center text-xs italic text-zinc-600">Noch keine Keys</li>
          )}
          {keysQ.data?.map((k) => (
            <li
              key={k.id}
              className={`flex items-center gap-2 rounded border border-zinc-800 p-2 text-xs ${
                k.revoked_at ? "opacity-40" : ""
              }`}
            >
              <div className="flex-1">
                <div className="font-medium text-zinc-200">{k.name}</div>
                <div className="mt-0.5 font-mono text-[10px] text-zinc-500">
                  tb_{k.prefix}_… · {k.scopes.join(", ")}
                </div>
                <div className="mt-0.5 text-[10px] text-zinc-600">
                  {new Date(k.created_at).toLocaleString("de-DE")}
                  {k.last_used_at && ` · zuletzt ${new Date(k.last_used_at).toLocaleString("de-DE")}`}
                  {k.revoked_at && ` · widerrufen ${new Date(k.revoked_at).toLocaleDateString("de-DE")}`}
                </div>
              </div>
              {!k.revoked_at && (
                <button
                  onClick={() => {
                    if (window.confirm(`Key "${k.name}" widerrufen?`)) revokeMut.mutate(k.id);
                  }}
                  className="text-zinc-500 hover:text-red-400"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
